import type { ActivityContext, ActivityHandle } from '../../core/activity';
import levelsData from './levels.json';

interface Rules {
  nbOfQuestions: number;
  fitLimits: boolean;
  range: number[];
  steps: number[];
  segments: number[];
  denominator?: number;
}

interface SubLevel {
  rules: Rules;
}

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  data: SubLevel[];
}

interface LevelsFile {
  levels: Level[];
}

interface RoundState {
  rangeMin: number;
  rangeMax: number;
  step: number;
  denominator: number;
  values: number[];
  target: number;
  cursorIdx: number;
  questionIndex: number;
  totalQuestions: number;
}

const LEVELS = (levelsData as LevelsFile).levels;

export function mountGraduatedLineUse(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  let subIndex = 0;
  let state: RoundState = buildRound(level.data[0]!.rules);
  let correctCount = 0;
  let totalCorrect = 0;
  let totalQuestionsTotal = 0;
  level.data.forEach((d) => { totalQuestionsTotal += d.rules.nbOfQuestions; });

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Use a régua</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage graduated">
      <div class="feedback" aria-live="polite"></div>
      <p class="graduated__progress" aria-live="polite"></p>
      <p class="graduated__prompt">Coloque o número <strong class="graduated__target"></strong> na régua.</p>
      <div class="graduated__ruler-wrap"></div>
      <button class="btn btn--primary graduated__ok" type="button">Confirmar</button>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.graduated__progress')!;
  const targetEl = page.querySelector<HTMLElement>('.graduated__target')!;
  const rulerWrap = page.querySelector<HTMLElement>('.graduated__ruler-wrap')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const okBtn = page.querySelector<HTMLButtonElement>('.graduated__ok')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  function renderProgress(): void {
    progressEl.textContent = `Pergunta ${state.questionIndex + 1} de ${state.totalQuestions}`;
  }

  function fmtValue(v: number): string {
    if (state.denominator === 1) return String(v);
    return (v / state.denominator).toLocaleString('pt-BR', { maximumFractionDigits: 3 });
  }

  function renderRuler(): void {
    rulerWrap.innerHTML = '';
    const W = 720;
    const H = 100;
    const padding = 40;
    const innerW = W - padding * 2;
    const n = state.values.length;
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', `0 0 ${W} ${H}`);
    svg.setAttribute('class', 'graduated__ruler');
    svg.setAttribute('role', 'group');

    // linha base
    const base = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    base.setAttribute('x1', String(padding));
    base.setAttribute('x2', String(padding + innerW));
    base.setAttribute('y1', String(H / 2));
    base.setAttribute('y2', String(H / 2));
    base.setAttribute('class', 'graduated__line');
    svg.appendChild(base);

    // Major tick: a cada stride; minor: resto.
    // Quando há muitas graduações, mostra label só nos majors pra não embolar.
    const maxLabels = 12;
    const stride = Math.max(1, Math.ceil((n - 1) / maxLabels));

    state.values.forEach((v, i) => {
      const x = padding + (innerW * i) / (n - 1);
      const isMajor = i === 0 || i === n - 1 || i % stride === 0;
      const tick = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      tick.setAttribute('x1', String(x));
      tick.setAttribute('x2', String(x));
      tick.setAttribute('y1', String(H / 2 - (isMajor ? 12 : 6)));
      tick.setAttribute('y2', String(H / 2 + (isMajor ? 12 : 6)));
      tick.setAttribute('class', `graduated__tick ${isMajor ? 'graduated__tick--major' : 'graduated__tick--minor'}`);
      svg.appendChild(tick);

      if (isMajor) {
        const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        label.setAttribute('x', String(x));
        label.setAttribute('y', String(H / 2 + 32));
        label.setAttribute('text-anchor', 'middle');
        label.setAttribute('class', 'graduated__label');
        label.textContent = fmtValue(v);
        svg.appendChild(label);
      }

      // área clicável invisível larga (mesma pra todos)
      const hit = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      hit.setAttribute('x', String(x - innerW / (n - 1) / 2));
      hit.setAttribute('y', '0');
      hit.setAttribute('width', String(innerW / (n - 1)));
      hit.setAttribute('height', String(H));
      hit.setAttribute('class', 'graduated__hit');
      hit.setAttribute('data-idx', String(i));
      svg.appendChild(hit);
    });

    // cursor (triangulo apontando pra baixo, em cima)
    const cursorX = padding + (innerW * state.cursorIdx) / (n - 1);
    const cursor = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
    cursor.setAttribute('points', `${cursorX - 10},${H / 2 - 30} ${cursorX + 10},${H / 2 - 30} ${cursorX},${H / 2 - 14}`);
    cursor.setAttribute('class', 'graduated__cursor');
    svg.appendChild(cursor);

    rulerWrap.appendChild(svg);

    // listeners
    svg.querySelectorAll<SVGRectElement>('.graduated__hit').forEach((rect) => {
      rect.addEventListener('click', () => {
        const idx = parseInt(rect.getAttribute('data-idx') ?? '0', 10);
        state.cursorIdx = idx;
        ctx.audio.play('click');
        renderRuler();
      });
    });
  }

  function renderTarget(): void {
    targetEl.textContent = fmtValue(state.target);
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 500);
  }

  function validate(): void {
    const value = state.values[state.cursorIdx]!;
    if (value === state.target) {
      ctx.audio.play('correct');
      showFeedback('ok');
      correctCount++;
      totalCorrect++;
      state.questionIndex++;
      if (state.questionIndex < state.totalQuestions) {
        nextQuestion();
      } else {
        subIndex++;
        if (subIndex < level.data.length) {
          state = buildRound(level.data[subIndex]!.rules);
          renderProgress();
          renderTarget();
          renderRuler();
        } else {
          window.setTimeout(finishLevel, 600);
        }
      }
    } else {
      ctx.audio.play('wrong');
      showFeedback('err');
    }
  }

  function nextQuestion(): void {
    // Mantém regras, sorteia novo target/cursor
    state.target = state.values[randInt(0, state.values.length - 1)]!;
    state.cursorIdx = Math.floor(state.values.length / 2);
    renderProgress();
    renderTarget();
    renderRuler();
  }

  okBtn.addEventListener('click', validate);

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'ArrowLeft' && state.cursorIdx > 0) {
      state.cursorIdx--;
      renderRuler();
      e.preventDefault();
    } else if (e.key === 'ArrowRight' && state.cursorIdx < state.values.length - 1) {
      state.cursorIdx++;
      renderRuler();
      e.preventDefault();
    } else if (e.key === 'Enter' || e.key === ' ') {
      validate();
      e.preventDefault();
    }
  };
  window.addEventListener('keydown', onKey);

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, totalCorrect);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você acertou ${totalCorrect} de ${totalQuestionsTotal} perguntas.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/graduated_line_use/${level.level + 1}`;
      });
    }
  }

  renderProgress();
  renderTarget();
  renderRuler();

  return {
    unmount(): void {
      window.removeEventListener('keydown', onKey);
      ctx.root.innerHTML = '';
    },
  };
}

function buildRound(rules: Rules): RoundState {
  const denominator = rules.denominator ?? 1;
  const step = rules.steps[randInt(0, rules.steps.length - 1)]!;
  const rangeMin = rules.range[0] ?? 0;
  const rangeMax = rules.range[1] ?? rangeMin + step * 5;

  let actualMax: number;
  if (rules.fitLimits) {
    actualMax = rangeMax;
  } else {
    const segMin = rules.segments[0] ?? 4;
    const segMax = rules.segments[1] ?? 8;
    const segCount = randInt(segMin, segMax);
    actualMax = rangeMin + segCount * step;
    if (actualMax > rangeMax) actualMax = rangeMax;
  }

  const values: number[] = [];
  for (let v = rangeMin; v <= actualMax; v += step) {
    values.push(v);
  }
  if (values.length < 2) values.push(rangeMin + step);

  const targetIdx = randInt(1, values.length - 2);
  const target = values[targetIdx] ?? values[0]!;

  return {
    rangeMin,
    rangeMax: actualMax,
    step,
    denominator,
    values,
    target,
    cursorIdx: Math.floor(values.length / 2),
    questionIndex: 0,
    totalQuestions: rules.nbOfQuestions,
  };
}

function randInt(min: number, max: number): number {
  if (max < min) return min;
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
